import { useRouter } from 'next/router';
import React, { useContext, useMemo } from 'react';
import { localeDomains } from 'src/config/localeDomains';
import { KaimonoWebRoutes } from 'src/config/routes';
import { I18nContext } from 'src/stores/I18n';
import { Presenter } from './Presenter';

export type BreadcrumbItem = {
  unprefixedPath: string;
  label: string;
  waWebRoutes?: boolean;
};

type Props = {
  items?: BreadcrumbItem[];
  isHomePage?: boolean;
};

export const Navigator: React.FC<Props> = ({ items: unfilteredItems, isHomePage }) => {
  const localeResources = useContext(I18nContext);
  const { locale } = useRouter();
  const domain = `${localeDomains(locale ?? 'en')}/kaimono`;
  const items = unfilteredItems?.filter(Boolean) as NonNullable<BreadcrumbItem>[];

  const getJsonLdForHomePage = (): object => ({
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: domain + KaimonoWebRoutes.index,
    name: localeResources.commons.wamazingShop,
    potentialAction: {
      '@type': 'SearchAction',
      target: domain + '/search?i={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  });

  const getJsonLdForOtherPages = (): object => ({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: localeResources.commons.wamazingShop,
        item: domain + KaimonoWebRoutes.index,
      } as const,
      ...items.map(
        (item, index) =>
          ({
            '@type': 'ListItem',
            position: 2 + index,
            name: item.label,
            item: domain + item.unprefixedPath,
          } as const)
      ),
    ],
  });

  const jsonLdMarkup = useMemo(() => {
    const list = isHomePage ? getJsonLdForHomePage() : getJsonLdForOtherPages();
    return JSON.stringify(list, null, 2);
  }, [items]);

  return <Presenter items={items} jsonLdMarkup={jsonLdMarkup} isHomePage={isHomePage} />;
};
